// =============================================================================
// NAVIGATION CONFIGURATION
// Centralized navigation config for desktop sidebar and mobile navigation
// =============================================================================

import type { UserRole, Permission } from '@twg/shared';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type NavSection = 'main' | 'tools' | 'alerts' | 'admin' | 'management';
export type BadgeType = 'alerts' | 'urgent' | 'stalled' | 'readyForPickup' | 'aging' | 'missedService' | 'noNextVisit' | 'routeNotScheduled' | 'noRoute' | 'emailUnread';

export interface NavItem {
  /** Route path for navigation */
  path: string;
  /** Emoji icon for the nav item */
  icon: string;
  /** Display label */
  label: string;
  /** Roles that can access this item (if undefined, all roles can access) */
  roles?: UserRole[];
  /** Permission required to access this item */
  permission?: Permission;
  /** Dynamic badge indicator */
  badge?: BadgeType;
  /** Section grouping for desktop sidebar */
  section?: NavSection;
  /** Whether this is a special item (e.g., menu trigger) */
  isSpecial?: boolean;
}

// -----------------------------------------------------------------------------
// Desktop Navigation Items
// -----------------------------------------------------------------------------

export const desktopNavItems: NavItem[] = [
  // MAIN section - Order: Dashboard, Schedules, Clients, Routes, Workorders, QA, Team
  {
    path: '/',
    icon: '🏠',
    label: 'Dashboard',
    section: 'main',
  },
  {
    path: '/schedules',
    icon: '📅',
    label: 'Schedules',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'main',
  },
  {
    path: '/clients',
    icon: '👥',
    label: 'Clients',
    section: 'main',
  },
  {
    path: '/routes',
    icon: '🗺️',
    label: 'Routes',
    section: 'main',
  },
  {
    path: '/workorders',
    icon: '📋',
    label: 'Work Orders',
    section: 'main',
  },
  {
    path: '/qa-reports',
    icon: '✅',
    label: 'QA',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'main',
  },
  {
    path: '/team',
    icon: '👷',
    label: 'Team',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'main',
  },
  // TOOLS section
  {
    path: '/admin/request-report',
    icon: '📊',
    label: 'Reports',
    section: 'tools',
  },
  {
    path: '/email',
    icon: '📧',
    label: 'Email',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    badge: 'emailUnread',
    section: 'tools',
  },
  {
    path: '/profile',
    icon: '⚙️',
    label: 'Settings',
    section: 'tools',
  },

  // ADMIN section (Error Analytics, Issue Triage, Schedule Editor removed - accessible from /admin page or /schedules)
  {
    path: '/admin',
    icon: '🛠️',
    label: 'Admin',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'admin',
  },
  {
    path: '/admin/inventory',
    icon: '🌿',
    label: 'Inventory',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'admin',
  },
  // MANAGEMENT section - module access
  {
    path: '/fleet',
    icon: '🚗',
    label: 'Fleet',
    permission: 'module:fleet',
    section: 'management',
  },
  {
    path: '/finance',
    icon: '💰',
    label: 'Finance',
    permission: 'module:finance',
    section: 'management',
  },
  {
    path: '/crm',
    icon: '📈',
    label: 'Sales',
    permission: 'module:crm',
    section: 'management',
  },
  {
    path: '/time',
    icon: '⏱️',
    label: 'Time',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'management',
  },
];

// -----------------------------------------------------------------------------
// Mobile Bottom Tabs
// -----------------------------------------------------------------------------

export const mobileBottomTabs: NavItem[] = [
  {
    path: '/',
    icon: '📍',
    label: 'Today',
  },
  {
    path: '/clients',
    icon: '👥',
    label: 'Clients',
  },
  {
    path: '/workorders',
    icon: '📋',
    label: 'Orders',
  },
  {
    path: '#menu',
    icon: '☰',
    label: 'Menu',
    isSpecial: true,
  },
];

// -----------------------------------------------------------------------------
// Mobile Menu Items (Hamburger Menu)
// -----------------------------------------------------------------------------

export const mobileMenuItems: NavItem[] = [
  // NAVIGATION section
  {
    path: '/schedules',
    icon: '📅',
    label: 'Schedules',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'main',
  },
  {
    path: '/routes',
    icon: '🗺️',
    label: 'Routes',
    section: 'main',
  },
  {
    path: '/qa-reports',
    icon: '✅',
    label: 'QA',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'main',
  },
  {
    path: '/team',
    icon: '👷',
    label: 'Team',
    section: 'main',
  },
  {
    path: '/time/my-timesheet',
    icon: '⏱️',
    label: 'My Timesheet',
    section: 'main',
  },
  {
    path: '/client-map',
    icon: '📍',
    label: 'Client Map',
    section: 'main',
  },

  // ALERTS section
  {
    path: '/urgent-workorders',
    icon: '🚨',
    label: 'Urgent WOs',
    badge: 'urgent',
    section: 'alerts',
  },
  {
    path: '/aging-clients',
    icon: '⏰',
    label: 'Aging Clients',
    badge: 'aging',
    section: 'alerts',
  },
  {
    path: '/routes-not-scheduled',
    icon: '📅',
    label: 'Route Not Scheduled',
    badge: 'routeNotScheduled',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'alerts',
  },
  {
    path: '/no-next-visit',
    icon: '📆',
    label: 'No Next Visit',
    badge: 'noNextVisit',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'alerts',
  },
  {
    path: '/missed-services',
    icon: '❌',
    label: 'Missed Services',
    badge: 'missedService',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'alerts',
  },

  // TOOLS section
  {
    path: '/admin/request-report',
    icon: '📊',
    label: 'Request Report',
    section: 'tools',
  },
  {
    path: '/admin',
    icon: '🛠️',
    label: 'Admin',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    section: 'tools',
  },
  {
    path: '/email',
    icon: '📧',
    label: 'Email',
    roles: ['Admin', 'Ops', 'SuperAdmin'],
    badge: 'emailUnread',
    section: 'tools',
  },
  {
    path: '/profile',
    icon: '⚙️',
    label: 'Settings',
    section: 'tools',
  },
];

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Filter navigation items based on user roles and permissions.
 * SuperAdmin bypasses all restrictions.
 */
export function filterNavItems(
  items: NavItem[],
  userRoles: UserRole[],
  userPermissions: Permission[],
  isSuperAdmin: boolean
): NavItem[] {
  return items.filter((item) => {
    // SuperAdmin bypasses all restrictions
    if (isSuperAdmin) {
      return true;
    }

    // Check role restrictions
    if (item.roles && item.roles.length > 0) {
      const hasRequiredRole = item.roles.some((role) => userRoles.includes(role));
      if (!hasRequiredRole) {
        return false;
      }
    }

    // Check permission restrictions
    if (item.permission) {
      const hasRequiredPermission = userPermissions.includes(item.permission);
      if (!hasRequiredPermission) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Group navigation items by section.
 */
export function groupBySection(items: NavItem[]): Record<NavSection, NavItem[]> {
  const groups: Record<NavSection, NavItem[]> = {
    main: [],
    tools: [],
    alerts: [],
    admin: [],
    management: [],
  };

  for (const item of items) {
    const section = item.section || 'main';
    groups[section].push(item);
  }

  return groups;
}

/**
 * Get navigation items for a specific section.
 */
export function getNavItemsBySection(items: NavItem[], section: NavSection): NavItem[] {
  return items.filter((item) => item.section === section);
}
